import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { DEMO_PROJECT_ID, DEMO_TWIN_PANEL_READINESS, DEMO_SEGMENTS } from '@/data/demo/preference-center/data'

interface AccuracyData {
  mean_accuracy: number
  wilson_lower: number
  wilson_upper: number
  p25_accuracy: number
  p50_accuracy: number
  p75_accuracy: number
  p90_accuracy: number
  twins_validated: number
  total_twins: number
  total_questions_tested?: number
  total_questions_correct?: number
  total_questions_abstained?: number
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true)
  return (
    <div className="border border-border-gray rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-deep-ink hover:bg-soft-panel/50 transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {title}
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" strokeWidth={1.75} /> : <ChevronDown className="h-4 w-4 text-slate-400" strokeWidth={1.75} />}
      </button>
      {open && <div className="px-4 pb-4 border-t border-border-gray">{children}</div>}
    </div>
  )
}

interface SegmentRow {
  segment_id: string
  name: string
  respondent_count: number
  accuracy: number | null
  wilson_lower: number | null
  wilson_upper: number | null
}

export function TwinAccuracyPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const [data, setData] = useState<AccuracyData | null>(null)
  const [segments, setSegments] = useState<SegmentRow[]>([])
  const [loading, setLoading] = useState(true)

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    if (isDemo) {
      setData(DEMO_TWIN_PANEL_READINESS)
      setSegments(DEMO_SEGMENTS.map(s => ({
        segment_id: s.segment_id,
        name: s.name,
        respondent_count: s.respondent_count,
        accuracy: s.accuracy ?? null,
        wilson_lower: s.wilson_lower ?? null,
        wilson_upper: s.wilson_upper ?? null,
      })))
      setLoading(false)
      return
    }
    Promise.all([
      fetch(`/api/v1/projects/${projectId}/twin-panel/readiness`, { headers })
        .then(r => r.ok ? r.json() as Promise<AccuracyData> : null).catch(() => null),
      fetch(`/api/v1/projects/${projectId}/twin-panel/segments`, { headers })
        .then(r => r.ok ? r.json() as Promise<{ segments: SegmentRow[] }> : { segments: [] }).catch(() => ({ segments: [] })),
    ]).then(([d, s]) => {
      setData(d)
      setSegments(s.segments ?? [])
    }).catch(() => {}).finally(() => setLoading(false))
  }, [projectId, isDemo])

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading accuracy data...</div>
  if (!data || data.twins_validated == null) return <div className="p-8 text-sm text-slate-500">No accuracy data yet for this project.</div>

  const pct = (n: number) => `${(n * 100).toFixed(0)}%`

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      {/* Headline */}
      <div>
        <h1 className="text-2xl font-medium text-deep-ink leading-tight" data-testid="twin-panel-headline">
          Your panel of {data.twins_validated.toLocaleString()} validated twins matches held-out human responses{' '}
          <strong>{pct(data.mean_accuracy)}</strong> of the time on average, with the top 20% of twins at {pct(data.p90_accuracy)}.
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Wilson 95% confidence range: {pct(data.wilson_lower)}–{pct(data.wilson_upper)} across {data.twins_validated.toLocaleString()} validated twins.
        </p>
        {data.total_questions_tested != null && (
          <p className="text-xs text-slate-400 mt-1" data-testid="abstention-summary">
            Scored {data.total_questions_correct?.toLocaleString() ?? '—'} of {data.total_questions_tested.toLocaleString()} questions
            {data.total_questions_abstained != null && data.total_questions_abstained > 0
              ? ` — abstained on ${data.total_questions_abstained.toLocaleString()} (${pct(data.total_questions_abstained / (data.total_questions_tested + data.total_questions_abstained))} abstention rate)`
              : ''
            } — {pct(data.mean_accuracy)} on scored
          </p>
        )}
      </div>

      {/* Percentile summary */}
      <Card>
        <div className="grid grid-cols-4 gap-4 text-center">
          {[
            { label: 'p25', value: pct(data.p25_accuracy) },
            { label: 'p50 (median)', value: pct(data.p50_accuracy) },
            { label: 'p75', value: pct(data.p75_accuracy) },
            { label: 'p90', value: pct(data.p90_accuracy) },
          ].map(p => (
            <div key={p.label}>
              <p className="text-2xl font-medium text-deep-ink">{p.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{p.label}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Expandable sections */}
      <div className="space-y-3">
        <Section title="What we measured">
          <p className="text-sm text-slate-600 pt-3 leading-relaxed">
            For each twin (one per respondent in your uploaded data), we randomly held out 20% of their answered prediction columns and ran every configured ensemble pass. Accuracy is the fraction of held-out answers where the prediction matched the respondent's actual answer.
          </p>
        </Section>

        <Section title="How we measured it">
          <div className="text-sm text-slate-600 pt-3 space-y-2 leading-relaxed">
            <p><strong>80/20 split:</strong> 80% of each twin's prediction responses are used as training context. The remaining 20% are held out as the test set.</p>
            <p><strong>Deterministic:</strong> The holdout split is seeded on the twin's ID and upload ID — running the pipeline twice on identical data produces identical accuracy numbers.</p>
            <p><strong>Wilson confidence interval:</strong> Accuracy is reported as a Wilson score interval at 95% confidence. This is more stable than a simple proportion for small holdout sets.</p>
            <p><strong>8 ensemble passes:</strong> Each pass uses a different prompting strategy. The reported accuracy is the best Wilson lower across all 8 passes for each twin.</p>
          </div>
        </Section>

        <Section title="Per-segment accuracy breakdown">
          <div className="pt-3 overflow-x-auto">
            {segments.length === 0 ? (
              <p className="text-sm text-slate-400">No segment data available yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-gray">
                    <th className="text-left py-2 pr-6 font-medium text-slate-500">Segment</th>
                    <th className="text-right py-2 pr-4 font-medium text-slate-500">Twins</th>
                    <th className="text-right py-2 font-medium text-slate-500">Mean accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {segments.map(seg => (
                    <tr key={seg.segment_id} className="border-b border-border-gray/40">
                      <td className="py-2 pr-6 text-slate-700">{seg.name}</td>
                      <td className="text-right py-2 pr-4 text-slate-500">{seg.respondent_count}</td>
                      <td className="text-right py-2 font-medium text-deep-ink">{seg.accuracy != null ? pct(seg.accuracy) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Section>

        <Section title="Confidence floor explanation">
          <div className="text-sm text-slate-600 pt-3 space-y-2 leading-relaxed">
            <p>The <strong>confidence floor</strong> is a minimum Wilson lower bound for a twin to be included in bulk queries. Default is 0.4.</p>
            <p>Twins whose best-config Wilson lower falls below this floor are marked <em>low confidence</em> and excluded from queries unless you opt in. This ensures bulk-query distributions aren't skewed by twins with weak accuracy signals.</p>
            <p>You can lower the floor in the bulk query composer to include more twins at the cost of wider prediction uncertainty.</p>
          </div>
        </Section>

        <Section title="What we don't claim">
          <div className="text-sm text-slate-600 pt-3 space-y-2 leading-relaxed">
            <p>Twin predictions are <strong>not human responses</strong>. They are model estimates grounded in the twin's actual prior responses.</p>
            <p>A twin's measured accuracy on their backtest is a calibration signal, not a guarantee for any new question.</p>
            <p>Final decisions remain with your team. Echosome surfaces signals and uncertainty — it does not approve campaigns.</p>
          </div>
        </Section>

        <Section title="Methodology references">
          <div className="text-sm text-slate-600 pt-3 space-y-2">
            <p>Confidence intervals: Wilson score interval (E.B. Wilson, 1927). See <em>Journal of the American Statistical Association</em>.</p>
            <p>Two-layer config selection: adapted from Park et al. (UIST 2023) — personalized ensemble prediction via historical accuracy + question-specific proxy adjustment.</p>
            <p>
              <Link to="/research/methodology/ablation" className="text-echo-indigo hover:underline">
                Read the full methodology page →
              </Link>
            </p>
          </div>
        </Section>
      </div>
    </div>
  )
}
